<template>
  <div class="schedule-days">
    <b-alert
      v-if="allowedDayLabels.length > 0"
      variant="light"
      show
      class="schedule-days__hint"
    >
      <strong>Bloqueo disponible en este rango:</strong>
      {{ allowedDaysMessage }}
      <div class="schedule-days__hint-tip">
        💡 Puedes definir un rango de horas de una vez (ej. 06:00 a 09:00) —
        no necesitas agregarlo hora por hora.
      </div>
    </b-alert>

    <div v-if="allowedDayLabels.length > 1" class="schedule-days__bulk">
      <CButton
        color="dark"
        size="sm"
        variant="outline"
        @click="blockAllDaysFull"
      >
        Bloquear día completo (06:00–20:00) en todos los días del rango
      </CButton>
    </div>

    <b-row
      v-for="(date, iDates) in dates"
      :key="iDates"
      class="schedule-days__row"
      :class="{ 'schedule-days__row--disabled': !isDayAllowed(date.day) }"
    >
      <b-col cols="8">
        <div class="form-check schedule-days__check">
          <input
            :id="`schedule-day-${iDates}`"
            class="form-check-input"
            type="checkbox"
            v-model="date.active"
            :disabled="!isDayAllowed(date.day)"
            @change="onDayToggle(date)"
          />
          <label class="form-check-label" :for="`schedule-day-${iDates}`">
            {{ date.day }}
          </label>
        </div>
      </b-col>

      <b-col cols="4" class="text-right">
        <CButton
          v-if="isDayAllowed(date.day)"
          color="secondary"
          size="sm"
          variant="ghost"
          class="schedule-days__icon-button"
          title="Bloquear el día completo (06:00–20:00)"
          @click="blockFullDay(date)"
        >
          Todo el día
        </CButton>
        <CButton
          v-if="isDayAllowed(date.day) && hasValidTimes(date) && allowedDayLabels.length > 1"
          color="info"
          size="sm"
          variant="ghost"
          class="schedule-days__icon-button"
          title="Copiar estos horarios a los demás días disponibles"
          @click="copyToOtherDays(date)"
        >
          <CIcon name="cil-copy" />
        </CButton>
        <CButton
          color="success"
          size="sm"
          variant="ghost"
          class="schedule-days__icon-button"
          :disabled="!isDayAllowed(date.day)"
          title="Agregar rango de horas"
          @click="addTime(date)"
        >
          <CIcon name="cil-plus" />
        </CButton>
      </b-col>

      <b-col lg="12">
        <p
          v-if="!isDayAllowed(date.day)"
          class="schedule-days__status schedule-days__status--disabled"
        >
          Fuera del rango consultado en el paso 1.
        </p>
        <p
          v-else-if="!date.active"
          class="schedule-days__status"
        >
          Disponible para bloqueo dentro del rango consultado.
        </p>

        <b-row v-for="(item, iTimes) in date.times" :key="iTimes">
          <b-col cols="5">
            <b-form-group>
              <v-select
                :options="timesFixed"
                v-model="item.start"
                @input="startTimeChanged($event, date, iTimes)"
                :clearable="false"
              >
              </v-select>
            </b-form-group>
          </b-col>
          -
          <b-col cols="5">
            <b-form-group>
              <v-select
                :options="endOptionsFor(item)"
                v-model="item.end"
                @input="endTimeChanged($event, date, iTimes)"
                :disabled="!item.start"
                :clearable="false"
              >
              </v-select>
            </b-form-group>
          </b-col>

          <b-col class="text-right">
            <CButton
              color="danger"
              size="sm"
              variant="ghost"
              @click="deleteTime(date.times, iTimes, date)"
            >
              <CIcon name="cil-trash" />
            </CButton>
          </b-col>
        </b-row>
      </b-col>

      <b-col lg="12">
        <hr />
      </b-col>
    </b-row>
  </div>
</template>

<script>

export default {
  name: "schedule-days",
  props: ["schedule", "times", "dates", "assignedServices", "selectedDateRange"],
  data() {
    return {
      // flagErr: false,
      // errMsg: 'No pueden haber horas vacías'
    };
  },
  created() {
    this.schedule["dates"] = this.dates;
  },
  watch: {
    selectedDateRange: {
      deep: true,
      immediate: true,
      handler() {
        this.sanitizeUnavailableDays();
      }
    }
  },
  computed: {
    // Horas de INICIO: solo horas en punto, de 06:00 a 19:00
    // (value 5 = 06:00; cada hora en punto es value 5 + 4n; 57 = 19:00).
    timesFixed() {
      if (!this.times) return [];
      return this.times.filter(
        t => (t.value - 5) % 4 === 0 && t.value >= 5 && t.value <= 57
      );
    },
    // Horas en punto candidatas a FIN: 07:00 a 20:00 (value 9..61).
    wholeHourEnds() {
      if (!this.times) return [];
      return this.times.filter(
        t => (t.value - 5) % 4 === 0 && t.value >= 9 && t.value <= 61
      );
    },
    allowedDayKeys() {
      if (
        !this.selectedDateRange ||
        !this.selectedDateRange.start ||
        !this.selectedDateRange.end
      ) {
        return [];
      }

      const start = new Date(this.selectedDateRange.start);
      const end = new Date(this.selectedDateRange.end);

      if (isNaN(start.getTime()) || isNaN(end.getTime())) {
        return [];
      }

      start.setHours(0, 0, 0, 0);
      end.setHours(0, 0, 0, 0);

      const allowedDays = new Set();
      const cursor = new Date(start);

      while (cursor <= end) {
        allowedDays.add(this.dayKeyFromDate(cursor));
        cursor.setDate(cursor.getDate() + 1);
      }

      return Array.from(allowedDays);
    },
    allowedDayLabels() {
      return this.dates
        .map(item => item.day)
        .filter(day => this.allowedDayKeys.includes(this.normalizeDayName(day)));
    },
    allowedDaysMessage() {
      if (this.allowedDayLabels.length === 1) {
        return `Solo puedes bloquear el ${this.allowedDayLabels[0]} porque es el unico dia incluido en el rango seleccionado.`;
      }

      return `Solo puedes bloquear estos dias: ${this.allowedDayLabels.join(", ")}.`;
    }
  },
  methods: {
    normalizeDayName(day) {
      const normalizedDay = (day || "").toLowerCase().trim();
      const dayMap = {
        "dom": "dom.",
        "dom.": "dom.",
        "lun": "lun.",
        "lun.": "lun.",
        "mar": "mar.",
        "mar.": "mar.",
        "mie": "mié.",
        "mie.": "mié.",
        "mié": "mié.",
        "mié.": "mié.",
        "jue": "jue.",
        "jue.": "jue.",
        "vie": "vie.",
        "vie.": "vie.",
        "sab": "sáb.",
        "sab.": "sáb.",
        "sáb": "sáb.",
        "sáb.": "sáb."
      };

      return dayMap[normalizedDay] || normalizedDay;
    },
    dayKeyFromDate(date) {
      const dayKeys = ["dom.", "lun.", "mar.", "mié.", "jue.", "vie.", "sáb."];
      return dayKeys[date.getDay()];
    },
    isDayAllowed(day) {
      return this.allowedDayKeys.includes(this.normalizeDayName(day));
    },
    notifyDatesChanged() {
      this.schedule.dates = this.dates;
      this.$emit("forbiddenDatesAndTimes", this.schedule.dates);
    },
    sanitizeUnavailableDays() {
      let hasChanges = false;

      this.dates.forEach(date => {
        if (!this.isDayAllowed(date.day) && (date.active || date.times.length > 0)) {
          date.active = false;
          date.times = [];
          hasChanges = true;
        }
      });

      if (hasChanges) {
        this.notifyDatesChanged();
      }
    },
    onDayToggle(date) {
      if (!date.active) {
        date.times = [];
      }

      this.notifyDatesChanged();
    },
    // Opciones de hora final para un rango: horas en punto POSTERIORES al inicio.
    endOptionsFor(item) {
      if (!item || !item.start) return [];
      return this.wholeHourEnds.filter(t => t.value > item.start.value);
    },
    hasValidTimes(date) {
      return (
        date.times.length > 0 &&
        date.times.every(t => t.start != null && t.end != null)
      );
    },
    startTimeChanged(time, date, i) {
      if (!this.isDayAllowed(date.day)) {
        date.times.splice(i, 1);
        date.active = false;
        this.notifyDatesChanged();
        return;
      }

      let existTime = null
      existTime = date.times.filter(timeItem =>
        timeItem.start.value == time.value)

      if(existTime.length>1){
        date.times.splice(i,1)
      }else{
        // Si el fin quedó inválido (vacío o <= inicio), sugerir inicio + 1 hora.
        // Si el usuario ya había elegido un fin mayor, se respeta (rangos multi-hora).
        const current = date.times[i];
        if (!current.end || current.end.value <= time.value) {
          current.end = this.times.find(t => t.value == time.value + 4);
        }
      }
      this.notifyDatesChanged();
    },
    endTimeChanged() {
      this.notifyDatesChanged();
    },
    // Bloquea el día completo (06:00–20:00) con un solo rango.
    blockFullDay(date) {
      if (!this.isDayAllowed(date.day)) return;
      const start = this.times.find(t => t.value === 5);
      const end = this.times.find(t => t.value === 61);
      date.active = true;
      date.times = [{ start, end }];
      this.notifyDatesChanged();
    },
    // Copia los rangos de un día a todos los demás días disponibles del rango.
    copyToOtherDays(sourceDate) {
      if (!this.hasValidTimes(sourceDate)) return;
      this.dates.forEach(date => {
        if (date === sourceDate || !this.isDayAllowed(date.day)) return;
        date.active = true;
        date.times = sourceDate.times.map(t => ({ start: t.start, end: t.end }));
      });
      this.notifyDatesChanged();
    },
    // Bloquea día completo en TODOS los días disponibles del rango.
    blockAllDaysFull() {
      const start = this.times.find(t => t.value === 5);
      const end = this.times.find(t => t.value === 61);
      this.dates.forEach(date => {
        if (!this.isDayAllowed(date.day)) return;
        date.active = true;
        date.times = [{ start, end }];
      });
      this.notifyDatesChanged();
    },
    addTime(date) {
      if (!this.isDayAllowed(date.day)) {
        return;
      }

      date.active = true;
      let item = {
        start: null,
        end: null,
        // walker: null
      };    
      date.times.push(item);
      this.notifyDatesChanged();
    },
    deleteTime(times, i, date) {
      times.splice(i,1)
      if(times.length == 0)
        date.active = false
      this.notifyDatesChanged()
    },
    
  }
};
</script>

<style lang="scss" scoped>
.schedule-days {
  &__hint {
    border-radius: 12px;
    border: 1px solid rgba(13, 110, 253, 0.12);
    background: linear-gradient(180deg, rgba(13, 110, 253, 0.06) 0%, rgba(13, 110, 253, 0.02) 100%);
    color: #365486;
  }

  &__hint-tip {
    margin-top: 0.4rem;
    font-size: 0.85rem;
  }

  &__bulk {
    margin-bottom: 1rem;
    text-align: right;
  }

  &__row {
    padding: 0.9rem 0.75rem 0 0.75rem;
    border-radius: 14px;
    transition: background-color 0.18s ease, opacity 0.18s ease;

    &--disabled {
      background-color: rgba(108, 117, 125, 0.04);
    }
  }

  &__check {
    font-weight: 600;
  }

  &__icon-button {
    min-width: 36px;
  }

  &__status {
    margin: 0.35rem 0 0.85rem;
    font-size: 0.88rem;
    color: #6c757d;

    &--disabled {
      color: #a0a7af;
    }
  }
}
</style>
